/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery
} from '@apollo/client'
import {
  ButtonBase,
  CircularProgress,
  Grow,
  Modal,
  Paper,
  Slide,
  Typography
} from '@material-ui/core'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import DateRangeIcon from '@material-ui/icons/DateRange'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import FilterListIcon from '@material-ui/icons/FilterList'
import { FC, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import { DateRangePicker } from '../../../components/DatePicker/DateRangePicker'
import { CHANNEL_INVENTORY_QUERY } from '../../../graphql/Inventory'
import { ACCOMMODATIONS_QUERY } from '../../../graphql/accommodation'
import { ROOMTYPES_QUERY } from '../../../graphql/roomtype'
import { DateObject, getDatesList } from '../../../helpers/date'
import { getDirection, getTranslate, lang } from '../../../localization'
import { Accommodation } from '../../../types/accommodation'
import { Channel } from '../../../types/channel'
import {
  ChannelInventoryData,
  CRSInventoryData,
  GetInventoryInput,
  MenuItemInInventory
} from '../../../types/inventory'
import { RoomType, RoomTypeBrief } from '../../../types/roomtype'
import { typeOfMenuItems } from '../InventoryRateplanRow'
import FilterModal from './FilterModal'
import InventoryDateRow from './InventoryDateRowMobile'
import InventoryTableProvider from './InventoryTableProvider'
import InventoryTableRatePlan from './InventoryTableRatePlan'
import InventoryTableRoomType from './InventoryTableRoomType'
import MenuModal from './MenuModal'
import PrimaryFilterModal from './PrimaryFilterModal'
import RatePlanTitle from './RateplanTitle'
import RowSkeleton from './Row.Skeleton'
import useStyles from './styles/index.styles'

interface InventoryMobileProps {
  getCRSInventory: LazyQueryExecFunction<
    {
      crsInventory: CRSInventoryData
    },
    OperationVariables
  >
  autoCompleteLabelAccommodation: any
  setautoCompleteLabelAccommodation: React.Dispatch<any>
  selectedDateInDatePicker: DateObject
  setselectedDateInDatePicker: React.Dispatch<React.SetStateAction<DateObject>>
  crsInventoryData: CRSInventoryData | undefined
  channels: Channel[]
  weekendIndexes: number[]
  setoccupancy: React.Dispatch<React.SetStateAction<number | 'def'>>
  occupancy: number | 'def'
  sethideZeroes: React.Dispatch<React.SetStateAction<boolean>>
  hideZeroes: boolean
  loadingForCRSInventory: boolean
  getInventoryInput: GetInventoryInput
  setgetInventoryInput: React.Dispatch<React.SetStateAction<GetInventoryInput>>
  loadingForChannel: boolean
  isRoleChannel: boolean | undefined
  isRoleProvider: boolean | undefined
}

const InventoryMobile: FC<InventoryMobileProps> = ({
  getInventoryInput,
  setgetInventoryInput,
  autoCompleteLabelAccommodation,
  setautoCompleteLabelAccommodation,
  getCRSInventory,
  selectedDateInDatePicker,
  setselectedDateInDatePicker,
  crsInventoryData,
  channels,
  sethideZeroes,
  hideZeroes,
  setoccupancy,
  occupancy,
  weekendIndexes,
  loadingForCRSInventory,
  loadingForChannel,
  isRoleChannel,
  isRoleProvider
}) => {
  const classes = useStyles()
  const [channelShows, setchannelShows] = useState<
    {
      roomtypeCode: string
      rateplans: { rateplanCode: string; showChannels: boolean }[]
    }[]
  >([])
  // const [autoCompleteLabelRoomType, setautoCompleteLabelRoomType] =
  //   useState<any>(null)
  // const [autoCompleteLabelProvider, setautoCompleteLabelProvider] =
  //   useState<any>(null)
  const [datePickerOpen, setdatePickerOpen] = useState<boolean>(false)
  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(true)
  const [filtersOpen, setfiltersOpen] = useState<boolean>(false)
  const [shownTable, setshownTable] = useState<
    'roomtypes' | 'providers' | 'rateplans'
  >('roomtypes')
  const [menuModalIsOpen, setmenuModalIsOpen] = useState<boolean>(false)
  const [
    selectedInventoryFieldInRatePlan,
    setselectedInventoryFieldInRatePlan
  ] = useState<MenuItemInInventory>({
    label: 'price',
    code: 'price',
    type: 'price'
  })
  const [menuItems, setmenuItems] = useState<MenuItemInInventory[]>(
    ['price', 'closed', 'cta', 'ctd', 'minStay', 'maxStay', 'extraBedPrice']
      .map((item) => ({
        label: item,
        code: item,
        type: typeOfMenuItems(item) as 'boolean' | 'number' | 'price'
      }))
      .concat(
        crsInventoryData?.roomTypes[0]?.basicDetails.providers[0]?.basicDetails.addOns.map(
          (addon) => ({
            label:
              lang == 'fa'
                ? addon.nameTranslatedList.find(
                    (name) => name.label === 'Iran, Islamic Republic of'
                  )?.value || ''
                : addon.name,
            code: addon.code,
            type: 'price'
          })
        ) || []
      )
  )

  const { data: accommodationsData, loading: loadingForAccommodationList } =
    useQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })
  const [getRoomTypes, { data: roomtypes, loading: loadingForRoomtypes }] =
    useLazyQuery<{
      roomTypes: RoomTypeBrief[]
    }>(ROOMTYPES_QUERY)
  useEffect(() => {
    if (shownTable === 'providers' && autoCompleteLabelAccommodation) {
      getRoomTypes({
        variables: {
          propertyCode: autoCompleteLabelAccommodation.code,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: false
        }
      })
    }
  }, [shownTable, autoCompleteLabelAccommodation])
  const datesList = useMemo(
    () => getDatesList(14, selectedDateInDatePicker.gregorianStartDate),
    [selectedDateInDatePicker.gregorianStartDate]
  )

  const [
    getChannelInventoryData,
    { data: channelInventoryData, loading: channelInventoryLoading }
  ] = useLazyQuery<{
    channelInventory: ChannelInventoryData[]
  }>(CHANNEL_INVENTORY_QUERY, {
    fetchPolicy: 'network-only'
  })

  const onClickRatePlans = (roomTypeCode: string, providerCode: string) => {
    getChannelInventoryData({
      variables: {
        input: {
          ...getInventoryInput,
          providerCode: providerCode,
          roomTypeCode: roomTypeCode
        }
      }
    })
  }
  const handleChangeSelectedInventoryFieldInRatePlan = (
    fieldType: MenuItemInInventory
  ) => {
    setselectedInventoryFieldInRatePlan(fieldType)
  }
  useEffect(() => {
    if (crsInventoryData && !!getInventoryInput.providerCode) {
      setmenuItems(
        ['price', 'closed', 'cta', 'ctd', 'minStay', 'maxStay', 'extraBedPrice']
          .map((item) => ({
            label: item,
            code: item,
            type: typeOfMenuItems(item) as 'boolean' | 'number' | 'price'
          }))
          .concat(
            crsInventoryData?.roomTypes[0]?.basicDetails.providers[0]?.basicDetails.addOns.map(
              (addon) => ({
                label:
                  lang == 'fa'
                    ? addon.nameTranslatedList.find(
                        (name) => name.label === 'Iran, Islamic Republic of'
                      )?.value || ''
                    : addon.name,
                code: addon.code,
                type: 'price'
              })
            ) || []
          )
      )
      setchannelShows(
        crsInventoryData.roomTypes.map((roomType) => ({
          roomtypeCode: roomType.basicDetails.roomTypeCode,
          rateplans:
            roomType.basicDetails.providers[0].basicDetails.ratePlans.map(
              (rateplan) => ({
                rateplanCode: rateplan.basicDetails.ratePlanCode,
                showChannels: false
              })
            )
        }))
      )
    }
  }, [crsInventoryData?.roomTypes, getInventoryInput.providerCode])

  return (
    <div>
      <div
        className={[
          classes.filtersButton,
          shownTable === 'rateplans' && !!getInventoryInput.roomTypeCode
            ? ''
            : classes.filtersButtonForOneButtonOnly
        ].join(' ')}>
        {shownTable === 'rateplans' && !!getInventoryInput.roomTypeCode ? (
          <ButtonBase
            className={classes.filterButton}
            onClick={() => setmenuModalIsOpen(true)}>
            <div>
              <Typography variant="body1">{getTranslate('Views')}</Typography>
              <Typography variant="body1" className={classes.submenu}>
                {getTranslate(selectedInventoryFieldInRatePlan.label)}
                {selectedInventoryFieldInRatePlan.type === 'price'
                  ? `: ${getInventoryInput.Currency}`
                  : null}
              </Typography>
            </div>

            <div>
              <ExpandMoreIcon />
            </div>
          </ButtonBase>
        ) : null}
        <ButtonBase
          className={classes.filterButton}
          onClick={() => setfiltersOpen(true)}>
          <Typography variant="body1">{getTranslate('Filter')}</Typography>
          <div>
            <FilterListIcon />
          </div>
        </ButtonBase>
      </div>

      {!loadingForCRSInventory
        ? crsInventoryData?.roomTypes.map((roomtype, index) => (
            <div
              className={classes.rowNameContainer}
              key={index}
              onClick={() => {
                if (shownTable !== 'providers') {
                  setshownTable('providers')

                  setgetInventoryInput({
                    ...getInventoryInput,
                    roomTypeCode: roomtype.basicDetails.roomTypeCode
                  })
                } else {
                  setshownTable('roomtypes')
                  setgetInventoryInput({
                    ...getInventoryInput,
                    roomTypeCode: ''
                  })
                }
              }}>
              <Typography variant="h4" className={classes.rowName}>
                {roomtype.basicDetails.name}
              </Typography>
              <Typography variant="h3" className={classes.rowName}>
                {roomtype.basicDetails.providers.length}
                {getTranslate(' Providers')}
              </Typography>
              <ArrowRightIcon
                className={[classes.channelArrowRight].join(' ')}
              />
            </div>
          ))
        : Array(3)
            .fill('')
            .map((_, index) => <RowSkeleton key={index.toString()} />)}
      {shownTable !== 'roomtypes' && (
        <div className={classes.dateButtonContainer}>
          <ButtonBase
            className={classes.dateButton}
            onClick={() => {
              setdatePickerOpen(true)
            }}>
            <Typography variant="body1">{getTranslate('Date')}</Typography>
            <div>
              <DateRangeIcon />
            </div>
          </ButtonBase>
        </div>
      )}
      <div className={classes.tableContainer}>
        <div className={classes.tableTitles}>
          {shownTable !== 'roomtypes' && (
            <div className={classes.bigDateContainer}>
              <Typography
                variant="h3"
                color="primary"
                className={classes.bigDate}>
                {lang === 'en'
                  ? datesList[0].gregorianDate.year
                  : datesList[0].jalaaliDate.year}
              </Typography>
            </div>
          )}
          {shownTable !== 'roomtypes' &&
            crsInventoryData?.roomTypes[0]?.basicDetails.providers.map(
              (provider) => (
                <div
                  className={classes.rowNameContainer}
                  key={provider.basicDetails.id}
                  onClick={() => {
                    if (shownTable !== 'rateplans') {
                      setshownTable('rateplans')
                      setgetInventoryInput({
                        ...getInventoryInput,
                        providerCode: provider.basicDetails.providerCode
                      })
                      onClickRatePlans(
                        getInventoryInput.roomTypeCode,
                        provider.basicDetails.providerCode
                      )
                    } else {
                      setshownTable('providers')
                      setgetInventoryInput({
                        ...getInventoryInput,
                        providerCode: ''
                      })
                    }
                  }}>
                  <Typography variant="h4" className={classes.rowName}>
                    {provider.basicDetails.name}
                  </Typography>

                  <ArrowRightIcon
                    className={[classes.channelArrowRight].join(' ')}
                  />
                </div>
              )
            )}
          {shownTable === 'rateplans' &&
            crsInventoryData?.roomTypes[0]?.basicDetails.providers[0]?.basicDetails.ratePlans.map(
              (ratePlanItem, index) => (
                <RatePlanTitle
                  key={index}
                  channelData={channelInventoryData?.channelInventory || []}
                  channels={channels}
                  ratePlanItem={ratePlanItem}
                  onClick={onClickRatePlans}
                  roomtypeCode={
                    crsInventoryData?.roomTypes[0].basicDetails.roomTypeCode ||
                    ''
                  }
                  providerCode={
                    crsInventoryData?.roomTypes[0]?.basicDetails.providers[0]
                      .basicDetails.providerCode || ''
                  }
                  showChannels={
                    channelShows.find(
                      (rt) => rt.roomtypeCode === getInventoryInput.roomTypeCode
                    )?.rateplans[0].showChannels || false
                  }
                  setshowChannels={() => {
                    const indexOfRoomType = channelShows.findIndex(
                      (rt) => rt.roomtypeCode === getInventoryInput.roomTypeCode
                    )
                    const indexOfRatePlan = channelShows[
                      indexOfRoomType
                    ].rateplans.findIndex(
                      (rp) =>
                        rp.rateplanCode ===
                        ratePlanItem.basicDetails.displayRatePlanCode
                    )
                    setchannelShows([
                      ...channelShows.slice(0, indexOfRoomType),
                      {
                        ...channelShows[indexOfRoomType],
                        rateplans: [
                          ...channelShows[indexOfRoomType].rateplans.slice(
                            0,
                            indexOfRatePlan
                          ),
                          {
                            ...channelShows[indexOfRoomType].rateplans[
                              indexOfRatePlan
                            ],
                            showChannels:
                              !channelShows[indexOfRoomType].rateplans[
                                indexOfRatePlan
                              ].showChannels
                          },
                          ...channelShows[indexOfRoomType].rateplans.slice(
                            indexOfRatePlan + 1
                          )
                        ]
                      },
                      ...channelShows.slice(indexOfRoomType + 1)
                    ])
                  }}
                />
              )
            )}
        </div>
        {shownTable !== 'roomtypes' && (
          <div className={classes.tableContent}>
            <InventoryDateRow
              {...{ selectedDateInDatePicker, setselectedDateInDatePicker }}
              range={'TWO_WEEKS'}
            />
            <InventoryTableProvider
              roomTypeData={crsInventoryData?.roomTypes[0]}
            />
            {shownTable === 'rateplans' &&
              crsInventoryData?.roomTypes[0]?.basicDetails.providers[0]?.basicDetails.ratePlans.map(
                (ratePlanItem, index) => (
                  <InventoryTableRatePlan
                    key={index}
                    {...{
                      weekendIndexes,
                      selectedInventoryFieldInRatePlan,
                      hideZeroes
                    }}
                    defaultOccupancy={
                      crsInventoryData?.roomTypes[0].basicDetails
                        .defaultOccupancy || 0
                    }
                    channelData={channelInventoryData?.channelInventory || []}
                    occupancy={occupancy}
                    ratePlanData={ratePlanItem}
                    showChannels={
                      channelShows.find(
                        (rt) =>
                          rt.roomtypeCode === getInventoryInput.roomTypeCode
                      )?.rateplans[0].showChannels || false
                    }
                    loadingForChannelInventory={channelInventoryLoading}
                  />
                )
              )}
          </div>
        )}
      </div>
      <Modal
        open={filterButtonSelected}
        dir={getDirection()}
        className={classes.filterForMobileSizeModal}
        onClose={() => setFilterButtonSelected(false)}>
        <Slide in={filterButtonSelected} direction="up" timeout={300}>
          <Paper className={classes.filterForMobileSize}>
            <PrimaryFilterModal
              accommodations={accommodationsData?.accommodations || []}
              {...{
                selectedDateInDatePicker,
                setselectedDateInDatePicker,
                getCRSInventory,
                setFilterButtonSelected,
                autoCompleteLabelAccommodation,
                setautoCompleteLabelAccommodation,
                shownTable,
                setshownTable,
                loadingForAccommodationList,
                getInventoryInput,
                setgetInventoryInput,
                isRoleChannel,
                isRoleProvider
              }}
            />
          </Paper>
        </Slide>
      </Modal>
      <Modal
        open={filtersOpen}
        dir={getDirection()}
        className={classes.filterForMobileSizeModal}
        onClose={() => setfiltersOpen(false)}>
        <Slide in={filtersOpen} direction="up" timeout={300}>
          <Paper className={classes.filterForMobileSize}>
            <FilterModal
              setFilterButtonSelected={setfiltersOpen}
              {...{
                getCRSInventory,
                autoCompleteLabelAccommodation,
                getInventoryInput,
                hideZeroes,
                occupancy,
                setautoCompleteLabelAccommodation,
                setgetInventoryInput,
                sethideZeroes,
                setoccupancy,
                setshownTable,
                shownTable,
                channels,
                loadingForChannel,
                isRoleChannel,
                isRoleProvider
              }}
            />
          </Paper>
        </Slide>
      </Modal>
      <Modal
        dir={getDirection()}
        open={menuModalIsOpen && shownTable === 'rateplans'}
        className={classes.menuModal}>
        <Slide
          in={menuModalIsOpen && shownTable === 'rateplans'}
          direction="down"
          timeout={600}>
          <Paper className={classes.menuModal}>
            <MenuModal
              setmenuModalIsOpen={setmenuModalIsOpen}
              items={menuItems}
              onChangeSelectedItem={(newMenuItem) =>
                handleChangeSelectedInventoryFieldInRatePlan(
                  newMenuItem as MenuItemInInventory
                )
              }
              activeItem={selectedInventoryFieldInRatePlan.code}
            />
          </Paper>
        </Slide>
      </Modal>
      {datePickerOpen && (
        <Grow in>
          <DateRangePicker
            onChange={(data: DateObject) => {
              setselectedDateInDatePicker(data)
            }}
            type={lang === 'fa' ? 'jalaali' : 'gregorian'}
            dateObject={selectedDateInDatePicker}
            onDoneClicked={() => setdatePickerOpen(false)}
            autoSelectedEndDate
            numberOfHoveredItems={14}
          />
        </Grow>
      )}
    </div>
  )
}
export default InventoryMobile
